/**
 * Contabilidade › Painel da Diretoria.
 *
 * O "Relatório Contábil" que a diretoria validou (resultado acumulado por operação;
 * atividade operacional pré e pós), montado do Resultado por Plano para o mês
 * fechado escolhido. "Modelo validado" mostra só o que o modelo tem; "Com melhorias"
 * acrescenta blocos marcados, cada um com o motivo da mudança.
 *
 * Exportar PDF usa a impressão do navegador: só o painel sai, uma folha A4 deitada
 * por página do modelo, com o carimbo "GNI - Inovação" e a data da exportação na
 * faixa verde do cabeçalho.
 * Backend: /contabilidade/diretoria (app/api/contabilidade/servico_diretoria.py).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Printer, MessageSquare } from "lucide-react";
import toast from "react-hot-toast";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { T, Botao, InfoTip } from "./ui";
import { CSS_PAINEL, montarPainel } from "./painelDiretoriaHtml";

const MES = ["", "janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
const rotuloMes = (m) => `${MES[Number(m.slice(5))].replace(/^./, (c) => c.toUpperCase())}/${m.slice(0, 4)}`;
const erroDe = (e, p) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : p; };

// Impressão: só o painel (some menu, cabeçalho do AURA e a barra desta tela), paisagem,
// segunda página do modelo em folha nova, carimbo na faixa verde de cada página.
const CSS_IMPRESSAO = `
@media print{
  @page{size:A4 landscape;margin:8mm}
  html,body{background:#fff!important}
  body *{visibility:hidden}
  body *:not(.pdir-imprime):not(.pdir-imprime *){background:transparent!important;box-shadow:none!important}
  .pdir-imprime,.pdir-imprime *{visibility:visible}
  .pdir-imprime{position:absolute;left:0;top:0}
  .pdir .pag{border:0;padding:0;margin:0}
  .pdir .pag{break-inside:avoid}
  .pdir .pag2{break-before:page}
  .pdir .so-tela{display:none!important}
  .pdir *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .pdir .carimbo-cab{display:block}
}`;

const TIP = "Relatório Contábil no formato validado pela diretoria, montado da contabilidade do ERP (Resultado por Plano, empresa inteira). Valores acumulados no ano até o mês escolhido, comparados com o mês anterior. \"Com melhorias\" acrescenta, sem tirar nada do modelo: o mês sozinho, o mesmo mês do ano anterior, o resultado completo mês a mês e a sinistralidade, cada um com o motivo.";

export default function PainelDiretoria() {
  const navigate = useNavigate();
  const { temNivel } = useAuth();
  const gestor = temNivel("resultado_plano", "gestor");
  const [meses, setMeses] = useState(null);
  const [mes, setMes] = useState("");
  const [d, setD] = useState(null);
  const [erro, setErro] = useState(null);
  const [soModelo, setSoModelo] = useState(false);
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState("");

  useEffect(() => {
    api.get("/contabilidade/diretoria/meses").then((r) => { setMeses(r.data); if (r.data.length) setMes(r.data[r.data.length - 1]); })
      .catch((e) => setErro(erroDe(e, "Não foi possível ler os meses da contabilidade.")));
  }, []);
  const carregar = () => {
    if (!mes) return;
    setD(null); setErro(null);
    api.get("/contabilidade/diretoria", { params: { ano: Number(mes.slice(0, 4)), mes: Number(mes.slice(5)) } })
      .then((r) => { setD(r.data); setTexto(r.data.comentario?.texto || ""); })
      .catch((e) => setErro(erroDe(e, "Não foi possível montar o painel.")));
  };
  useEffect(carregar, [mes]); // eslint-disable-line

  // o carimbo ganha a data e a hora no momento da exportação (beforeprint, abaixo)
  const html = useMemo(() => (d ? montarPainel({ ...d, carimbo: "GNI - Inovação · AURA" }) : ""), [d]);
  // Cada página do modelo cabe numa folha A4 deitada (área útil ~1050 x 715 px com
  // margem de 8 mm): antes de imprimir, monta o painel na largura de tela cheia,
  // mede a página mais alta e reduz a escala. Depois volta ao normal.
  const folha = useRef(null);
  useEffect(() => {
    const LARG = 1400;
    const antes = () => {
      const el = folha.current; if (!el) return;
      const hora = new Date().toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
      el.querySelectorAll(".carimbo-cab").forEach((c) => { c.textContent = `GNI - Inovação · AURA · exportado em ${hora}`; });
      el.style.width = `${LARG}px`;
      const alt = Math.max(...[...el.querySelectorAll(".pag")].map((p) => p.scrollHeight), 1);
      el.style.zoom = String(Math.min(1, 1050 / LARG, 715 / alt));
    };
    const depois = () => { const el = folha.current; if (el) { el.style.width = ""; el.style.zoom = ""; } };
    window.addEventListener("beforeprint", antes); window.addEventListener("afterprint", depois);
    return () => { window.removeEventListener("beforeprint", antes); window.removeEventListener("afterprint", depois); };
  }, []);
  const salvar = async () => {
    try {
      await api.put(`/contabilidade/diretoria/${d.ano}/${d.mes}/comentario`, { texto });
      toast.success("Comentário salvo"); setEditando(false); carregar();
    } catch (e) { toast.error(erroDe(e, "Não foi possível salvar.")); }
  };
  // links das recomendações (vêm no HTML como data-href)
  const clique = (e) => { const a = e.target.closest("[data-href]"); if (a) navigate(a.getAttribute("data-href")); };

  return (
    <div style={{ padding: "20px 24px", background: T.bg, minHeight: "100%" }}>
      <style>{CSS_PAINEL + CSS_IMPRESSAO}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <h1 style={{ margin: 0, fontSize: 21, color: T.ink }}>Painel da Diretoria</h1>
        <InfoTip w={380} texto={TIP} />
        <select value={mes} onChange={(e) => setMes(e.target.value)} aria-label="Mês fechado"
                style={{ border: `1px solid ${T.line}`, borderRadius: 8, padding: "6px 10px", fontSize: 13, background: "#fff" }}>
          {(meses || []).slice().reverse().map((m) => <option key={m} value={m}>{rotuloMes(m)}</option>)}
        </select>
        <div style={{ display: "inline-flex", border: `1px solid ${T.line}`, borderRadius: 8, overflow: "hidden" }}>
          {[[true, "Modelo validado"], [false, "Com melhorias"]].map(([v, r]) => (
            <button key={r} onClick={() => setSoModelo(v)} style={{ border: 0, padding: "6px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer",
              background: soModelo === v ? T.ink : "#fff", color: soModelo === v ? "#fff" : T.ink }}>{r}</button>))}
        </div>
        <span style={{ fontSize: 12.5, color: T.inkSoft }}>Contabilidade do ERP (Resultado por Plano), empresa inteira.</span>
        <div style={{ flex: 1 }} />
        {gestor && d && <Botao pequeno onClick={() => setEditando(!editando)}><MessageSquare size={14} /> Comentário do analista</Botao>}
        <Botao pequeno primario onClick={() => window.print()} disabled={!d}><Printer size={14} /> Exportar PDF</Botao>
      </div>

      {editando && d && (
        <div style={{ background: "#fff", border: `1px solid ${T.line}`, borderRadius: 12, padding: 12, marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Comentário do analista ({rotuloMes(mes)})</div>
          <div style={{ fontSize: 12, color: T.inkSoft, marginBottom: 6 }}>Aparece na Análise por Operação, no item da Operação Assistencial. Ex.: quais despesas mais cresceram no mês.</div>
          <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={3} maxLength={2000}
                    style={{ width: "100%", boxSizing: "border-box", border: `1px solid ${T.line}`, borderRadius: 8, padding: 8, fontFamily: "inherit" }} />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}><Botao pequeno primario onClick={salvar}>Salvar</Botao><Botao pequeno onClick={() => setEditando(false)}>Cancelar</Botao>
            {d.comentario && <span style={{ fontSize: 12, color: T.inkSoft, alignSelf: "center" }}>Último: {d.comentario.autor}</span>}</div>
        </div>
      )}

      {erro && <div style={{ background: T.riscoSoft, color: T.risco, borderRadius: 10, padding: "10px 14px", fontSize: 13 }}>{erro}</div>}
      {meses && meses.length === 0 && <div style={{ color: T.inkSoft }}>Ainda não há contabilidade carregada no Resultado por Plano.</div>}
      {!d && !erro && meses?.length !== 0 && <div style={{ color: T.inkSoft }}>Montando o painel…</div>}
      {d && (
        <div className="pdir-imprime" ref={folha}>
          {/* HTML montado só de números do endpoint; o comentário passa por esc() */}
          <div className={`pdir${soModelo ? " modelo" : ""}`} onClick={clique} dangerouslySetInnerHTML={{ __html: html }} />
        </div>
      )}
    </div>
  );
}
